import { ComparisonDelta } from "@/analytics/components/ComparisonDelta";
import { ExploreContributionPie } from "@/analytics/components/ExploreContributionPie";
import { CompareSelect } from "@/analytics/components/CompareSelect";
import { PeriodSelect } from "@/analytics/components/PeriodSelect";
import type { ExploreMetricView } from "@/analytics/core/types";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader, PageShell } from "@/components/layout/PageHeader";
import { Card, CardBody, CardHeader, InlineError, Spinner } from "@/components/ui";
import { formatMetricValue } from "@/grid/core/format-metric";
import {
  findAccessibleTeamBySlug,
  useAccessibleTeams,
} from "@/team-dashboard/hooks/useAccessibleTeams";
import { useTeamDashboardData } from "@/team-dashboard/hooks/useTeamDashboardData";
import { useTeamDashboardParams } from "@/team-dashboard/hooks/useTeamDashboardParams";
import { teamRouteForName } from "@/team-dashboard/lib/team-slug";
import { cn } from "@/lib/cn";
import { Navigate, useParams } from "react-router-dom";

function MetricTile({ view, compareLabel }: { view: ExploreMetricView; compareLabel: string }) {
  const hasBreakdown = view.contributions.length > 1;

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <h2 className="text-sm font-medium text-[var(--color-text)]">{view.metric.label}</h2>
      </CardHeader>
      <CardBody className="flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          {hasBreakdown ? (
            <ExploreContributionPie
              slices={view.contributions}
              format={view.metric.format}
              part="pie"
              size={140}
            />
          ) : (
            <p className="text-sm text-[var(--color-text-muted)]">
              No rep breakdown for this period.
            </p>
          )}

          <div className="flex shrink-0 flex-col items-end gap-2 text-right">
            <p className="text-3xl font-semibold leading-none tracking-tight text-[var(--color-text)] sm:text-4xl">
              {formatMetricValue(view.total, view.metric.format)}
            </p>
            <ComparisonDelta
              deltaPct={view.deltaPct}
              compareLabel={compareLabel}
              className="max-w-[9rem] flex-col items-end gap-1 sm:max-w-[10rem]"
            />
          </div>
        </div>

        {hasBreakdown ? (
          <ExploreContributionPie
            slices={view.contributions}
            format={view.metric.format}
            part="legend"
            legendLayout="stacked"
            className="border-t border-[var(--color-border)] pt-3"
          />
        ) : null}
      </CardBody>
    </Card>
  );
}

export function TeamDashboardPage() {
  const { teamSlug = "" } = useParams<{ teamSlug: string }>();
  const { data: accessibleTeams, isLoading: teamsLoading } = useAccessibleTeams();
  const team = findAccessibleTeamBySlug(accessibleTeams, teamSlug);
  const { periodSpec, compareMode, period, compare, updateParams } = useTeamDashboardParams();
  const { data, isLoading, isFetching, error } = useTeamDashboardData(
    team?.teamGroupKey ?? "",
    period,
    compare,
  );

  if (!teamsLoading && accessibleTeams && accessibleTeams.length === 0) {
    return <Navigate to="/company/sales" replace />;
  }

  if (!teamsLoading && accessibleTeams && !team) {
    const fallback = accessibleTeams[0];
    if (fallback) {
      return <Navigate to={teamRouteForName(fallback.teamName)} replace />;
    }
    return <Navigate to="/company/sales" replace />;
  }

  return (
    <AppLayout>
      <PageShell>
        <PageHeader
          title={data?.teamName ?? team?.teamName ?? "Team Dashboard"}
          actions={
            <PeriodSelect
              periodSpec={periodSpec}
              onChange={(next) => updateParams({ periodSpec: next })}
              trailing={
                <CompareSelect
                  value={compareMode}
                  onChange={(next) => updateParams({ compareMode: next })}
                />
              }
            />
          }
        />

        {isLoading && !data ? (
          <div className="flex justify-center py-16">
            <Spinner label="Loading team dashboard…" />
          </div>
        ) : null}

        {error ? (
          <InlineError
            message={error instanceof Error ? error.message : "Failed to load team dashboard"}
          />
        ) : null}

        {data ? (
          <div
            className={cn(
              "grid gap-4 sm:grid-cols-2 xl:grid-cols-3",
              "transition-opacity duration-200",
              isFetching ? "opacity-60" : "opacity-100",
            )}
          >
            {data.metrics.map((view) => (
              <MetricTile key={view.metric.id} view={view} compareLabel={compare.label} />
            ))}
          </div>
        ) : null}
      </PageShell>
    </AppLayout>
  );
}

export function TeamDashboardIndexPage() {
  const { data: accessibleTeams, isLoading } = useAccessibleTeams();

  if (isLoading) {
    return (
      <AppLayout>
        <PageShell>
          <div className="flex justify-center py-16">
            <Spinner label="Loading teams…" />
          </div>
        </PageShell>
      </AppLayout>
    );
  }

  const first = accessibleTeams?.[0];
  if (first) {
    return <Navigate to={teamRouteForName(first.teamName)} replace />;
  }

  return <Navigate to="/company/sales" replace />;
}
